import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentsService } from '@references/shared/services/environments.service';
import { PublicVerificationRequestsService } from '@references/shared/services/public/verification-requests.service';
import { IAPIErrorObject } from '@shared/interfaces/api.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { IVerificationRequest } from '@shared/interfaces/verification-request.interface';
import { LoggerService } from '@shared/services/logger.service';
import { EmailValidityValidator } from '@shared/validators/emailValidity.validators';
import { PhoneEmailRequiredValidator } from '@shared/validators/phoneEmailRequired.validators';
import { SessionStorage } from 'ngx-webstorage';

@Component({
  selector: 'app-verification-requests',
  templateUrl: './verification-requests.component.html'
})
export class VerificationRequestsComponent implements OnInit, OnDestroy {
  @SessionStorage() public hiringFirm: IHiringFirm;
  @SessionStorage() private settings: ISetting;

  public entry: FormGroup;
  public id: string;
  public items: FormArray;
  public verificationRequest: IVerificationRequest;
  public counterMinimum = Number(2);
  public language = String('en');
  public params: any = {};

  public isSubmitting = Boolean(false);
  public isFilled = Boolean(false);
  public isCollapsed = Boolean(true);
  public isSubmitted = Boolean(false);

  public error: IAPIErrorObject;
  public errors: any = [];

  public bsConfig: any = {
    containerClass: 'v-datepicker v-datepicker-range',
    dateInputFormat: 'll',
    rangeInputFormat: 'll',
    maxDate: new Date(),
    showWeekNumbers: false,
    adaptivePosition: true
  };

  public submittedCTA = String(
    `${this._environments.ENDPOINTS_APP}/register?returnUrl=references`
  );

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _ar: ActivatedRoute,
    private readonly _logger: LoggerService,
    private readonly _environments: EnvironmentsService,
    private readonly _fb: FormBuilder,
    private readonly _publicVerificationRequests: PublicVerificationRequestsService,
    private readonly _translate: TranslateService,

    private readonly _emailValidityValidator: EmailValidityValidator
  ) {}

  public get fieldsList() {
    let html = '<ul class="list-unstyled text-start m-0 p-2">';
    const fields = [];
    const entryControls = this.entry['controls'];
    const verificationsControls = (
      this.entry.get('verifications') as FormGroup
    )['controls'];

    for (const name in entryControls) {
      if (entryControls[name].invalid && name === 'isAccepted') {
        fields.push({
          label: `VERIFICATION_REQUESTS.FIELDS.${name.toUpperCase()}`
        });
      }
    }

    for (const name in verificationsControls) {
      if (verificationsControls[name].invalid) {
        fields.push({
          label: `VERIFICATION_REQUESTS.FIELDS.VERIFICATION`,
          value: Number(name) + 1
        });
      }
    }

    fields.map((f: any) => {
      this._translate.get(f.label, f).subscribe((res: string) => {
        html += `<li><i class="lnr lnr-cross"></i> ${res}</li>`;
      });
    });

    html += '</ul>';

    return html;
  }

  public get verificationsFg(): FormArray {
    return this.entry.get('verifications') as FormArray;
  }

  ngOnInit(): void {
    this.id = this._ar.snapshot.params.id;
    this.createForm();
    this.error = {};

    this.getVerificationRequest();
  }

  ngOnDestroy(): void {}

  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      this.isSubmitting = true;

      const url = `POST /public/verification_requests/${this.id}`;
      this._publicVerificationRequests.post(this.id, value).subscribe(
        (res: IVerificationRequest) => {
          this._logger.info(this.constructorName, url, res);

          this.isSubmitted = true;
          this.isSubmitting = false;
        },
        (err) => {
          this._logger.error(this.constructorName, url, err);
          this.isSubmitted = false;
          this.isSubmitting = false;
        }
      );
    }
  }

  public updateNotApplicable(type: string, fg: FormGroup): void {
    const value = fg.get('verifier').get(`${type}_not_applicable`).value;
    const control = (fg.get('verifier') as FormGroup).controls[type];

    if (value) {
      control.disable();
      control.setValidators([]);
    } else {
      control.setValidators([Validators.required]);
      control.enable();
    }

    fg.get('verifier').get(type).patchValue('');
    control.updateValueAndValidity();
  }

  public addItem() {
    this.items = this.entry.get('verifications') as FormArray;
    this.items.push(this.createItem());
  }

  public deleteItem(index: number) {
    const control = this.entry.controls['verifications'] as FormArray;
    control.removeAt(index);
  }

  public isEmailRequired(v: FormGroup) {
    const control = v.get('verifier').get('email') as FormGroup;
    const validator = control.validator({} as AbstractControl);
    return validator?.required;
  }

  public isPhoneRequired(v: any) {
    const control = v.get('verifier').get('phone') as FormGroup;
    const validator = control.validator({} as AbstractControl);
    return validator?.required;
  }

  public getVerificationRequest() {
    const url = `GET /public/verification_requests/${this.id}`;
    this._publicVerificationRequests.get(this.id).subscribe(
      (res: IVerificationRequest) => {
        this._logger.info(this.constructorName, url, res);

        this.verificationRequest = res;

        this.isFilled = this.verificationRequest.filled;

        this.hiringFirm = this.verificationRequest.hiringFirm;
        this.settings = this.hiringFirm.settings;

        if (this.isFilled && this.verificationRequest.candidate) {
          const profile = this.verificationRequest.candidate.profile;

          if (profile?.is_active) {
            this.submittedCTA = `${this._environments.ENDPOINTS_APP}/references`;
          }
        } else {
          if (this.verificationRequest.candidate) {
            this.params = {
              full_name: this.verificationRequest.candidate.full_name,
              minimum_verifications:
                this.verificationRequest.minimum_verifications
            };
          }

          this.counterMinimum =
            this.verificationRequest.minimum_verifications > 0
              ? this.verificationRequest.minimum_verifications
              : this.counterMinimum;

          for (let i = 0; i < this.counterMinimum; i++) {
            this.addItem();
          }
        }
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.error = err[0];
      }
    );
  }

  public updateCurrentDate(fg: FormGroup): void {
    const value = (fg.get('experience') as FormGroup).get('isCurrent').value;
    const control = (fg.get('experience') as FormGroup).controls['end_date'];

    if (value) {
      control.disable();
      control.setValidators([]);
    } else {
      control.setValidators([Validators.required]);
      control.enable();
    }

    (fg.get('experience') as FormGroup).get('end_date').patchValue(null);
    control.updateValueAndValidity();
  }

  private createItem(): FormGroup {
    return this._fb.group({
      experience: this._fb.group({
        company: ['', [Validators.required]],
        position: ['', [Validators.required]],
        start_date: ['', [Validators.required]],
        end_date: [''],
        isCurrent: [false, [Validators.required]]
      }),
      verifier: this._fb.group(
        {
          first_name: ['', [Validators.required]],
          last_name: ['', [Validators.required]],
          title: ['', [Validators.maxLength(63)]],
          title_not_applicable: [false],
          company_name: ['', [Validators.required]],
          company_name_not_applicable: [false],
          email: [
            ''
            // [this._emailValidityValidator.validates]
          ],
          phone: [''],
          countryInitial: ['US'],
          country_code: ['1', [Validators.required]],
          reason: ['U'],
          employment_status: ['U']
        },
        {
          validator: PhoneEmailRequiredValidator.DynamicRequired(
            'phone',
            'email',
            false
          )
        }
      )
    });
  }

  private createForm() {
    this.entry = this._fb.group({
      id: [this.id, [Validators.required]],
      verifications: this._fb.array([]),
      isAccepted: [false, [Validators.required, Validators.pattern('true')]]
    });
  }
}
